import { isFutureDate, isPastDate, parseDate } from '../dates';
import { isZipCode, isEmail, isPhoneNumber } from '../text';
import { isFn } from '../types';
import { isURL } from '../url';
/** Compose multiple {@linkcode FormFieldValidation} functions into a single one. */
export function composeFieldValidators(...validations) {
    const filtered = validations.filter(isFn);
    return value => {
        for (const fn of filtered) {
            const feedback = fn(value);
            if (feedback instanceof Promise) {
                return feedback;
            }
            if (typeof feedback === 'string') {
                return feedback;
            }
        }
        return undefined;
    };
}
/**
 * Composes a validation function for an optional form field.
 * If the field value is empty or `undefined`, the validation is skipped.
 * Otherwise, it applies the provided validation functions in sequence.
 *
 * @param validations - A list of validation functions to apply to the field value.
 * @returns A validation function that takes a field value and returns a validation error or `undefined` if the value is valid or the field is empty.
 */
export function composeOptionalField(...validations) {
    return value => {
        if (!value) {
            return;
        }
        const validate = composeFieldValidators(...validations);
        return validate(value);
    };
}
/** Checks whether the given field is a date-time in the future. */
export const shouldBeDateTimeField = value => {
    const parsed = parseDate(value);
    if (!parsed.isValid) {
        return `Should be a date-time`;
    }
    return undefined;
};
/** Checks whether the given field is a date-time in the future. */
export const shouldBeFutureDateTimeField = value => {
    const parsed = parseDate(value);
    if (!parsed.isValid) {
        return `Should be a date-time`;
    }
    if (isPastDate(parsed)) {
        return `Should be a date in the future`;
    }
    return undefined;
};
/** Checks whether the given field is a date-time in the past. */
export const shouldBePastDateTimeField = value => {
    const parsed = parseDate(value);
    if (!parsed.isValid) {
        return `Should be a date-time`;
    }
    if (isFutureDate(parsed)) {
        return `Should be a date in the past`;
    }
    return undefined;
};
export const PASSWORD_FORMAT_REGEX = 
// eslint-disable-next-line no-useless-escape
/^(?=.*[0-9])(?=.*[!@#$%^&*\-\(\)])[a-zA-Z0-9!@#$%^&*\-\(\)]{8,}$/i;
/**
 * Validates if the given value meets the password format requirements.
 *
 * The password must contain at least one uppercase letter, one lowercase letter,
 * one number, and one special character.
 *
 * @param value - The password string to validate.
 * @returns A validation error message if the password does not meet the requirements, otherwise undefined.
 */
export const shouldBePassword = value => {
    if (Boolean(value) && !PASSWORD_FORMAT_REGEX.test(value)) {
        return 'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character';
    }
    return undefined;
};
/**
 * Validates whether a given string is a valid email address.
 *
 * @param value - The string to be validated as an email address.
 * @returns A string with an error message if the validation fails, otherwise undefined.
 */
export const shouldBeEmail = value => {
    if (Boolean(value) && !isEmail(value)) {
        return `"${value}" is not a valid email address`;
    }
    return undefined;
};
/**
 * Validates whether a given string is a valid phone number.
 *
 * This function checks the input value against a predefined phone number format using a regular expression (`PHONE_FORMAT_REGEX`).
 * If the value does not match the format, it returns an error message indicating that the input is not a valid phone number. Otherwise, it returns `undefined`, indicating that the validation passed.
 *
 * @param value - The string value to validate as a phone number.
 * @returns A string error message if the validation fails, or `undefined` if it passes.
 */
export const shouldBePhone = value => {
    if (Boolean(value) && !isPhoneNumber(value)) {
        return `"${value}" is not a valid phone number`;
    }
    return undefined;
};
/**
 * Validates whether a given string is a valid zip code.
 *
 * @param value - The string value to validate as a zip code.
 * @returns A validation error message if the value is not a valid zip code, or `undefined` if the value is valid.
 */
export const shouldBeZipCode = value => {
    if (Boolean(value) && !isZipCode(value)) {
        return `"${value}" is not a valid zip code`;
    }
    return undefined;
};
/**
 * Validates whether a given string is a valid URL.
 *
 * @param value - The string value to validate.
 * @returns An error message if the value is not a valid URL, or `undefined` if the validation passes.
 */
export const shouldBeURL = value => {
    if (Boolean(value) && !isURL(value)) {
        return `"${value}" is not a valid URL`;
    }
    return undefined;
};
